import { CanceledError, type AxiosRequestConfig } from "axios";
import { apiClient } from "./client";
import { getAccessToken } from "./auth";

export type QuoteReadIdentity = {
  tenantId: string;
  userId: string;
  role: string;
};
export type QuoteReadCredential = { token: string | null; fallback: string };
export type QuoteReadAccess = {
  credential: QuoteReadCredential;
  owns: () => boolean;
};
export function captureQuoteReadCredential(): QuoteReadCredential {
  const token = getAccessToken();
  return {
    token,
    fallback: token
      ? ""
      : JSON.stringify([
          localStorage.getItem("cnc.tenant_id"),
          localStorage.getItem("cnc.user_id"),
          localStorage.getItem("cnc.role"),
        ]),
  };
}
export function matchesQuoteReadIdentity(
  value: unknown,
  identity: QuoteReadIdentity,
): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const user = value as Record<string, unknown>,
    tenant = user.tenant;
  return (
    !!identity.tenantId &&
    !!identity.userId &&
    !!identity.role &&
    !!tenant &&
    typeof tenant === "object" &&
    !Array.isArray(tenant) &&
    (tenant as Record<string, unknown>).id === identity.tenantId &&
    user.id === identity.userId &&
    user.role === identity.role
  );
}
export function quoteReadCredentialCurrent(
  captured: QuoteReadCredential,
): boolean {
  return (
    JSON.stringify(captureQuoteReadCredential()) === JSON.stringify(captured)
  );
}
export function quoteReadRequestConfig(
  credential: QuoteReadCredential,
  signal?: AbortSignal,
  owns?: () => boolean,
): AxiosRequestConfig {
  return {
    signal,
    _silentError: true,
    // axios 的 request 拦截器已注入实际头；transform 紧邻每次 dispatch，包括401后的重发。
    transformRequest: [
      (data, headers) => {
        if (!quoteReadCredentialCurrent(credential) || (owns && !owns()))
          throw new CanceledError("报价读取的身份归属已变化");
        if (credential.token) {
          if (headers.get("Authorization") !== `Bearer ${credential.token}`)
            throw new CanceledError("报价读取的凭证已变化");
        } else {
          const [tenant, user, role] = JSON.parse(credential.fallback);
          if (
            headers.get("Authorization") ||
            (tenant &&
              user &&
              (headers.get("X-Tenant-Id") !== tenant ||
                headers.get("X-User-Id") !== user ||
                headers.get("X-User-Role") !== (role ?? "platform_ops")))
          )
            throw new CanceledError("报价读取的开发身份已变化");
        }
        return data;
      },
    ],
  };
}
export async function quoteReadGet<T>(
  path: string,
  credential: QuoteReadCredential,
  params?: unknown,
  signal?: AbortSignal,
  owns?: () => boolean,
): Promise<T> {
  const { data } = await apiClient.get<T>(path, {
    ...quoteReadRequestConfig(credential, signal, owns),
    params,
  });
  if (
    !quoteReadCredentialCurrent(credential) ||
    signal?.aborted ||
    (owns && !owns())
  )
    throw new CanceledError("报价读取的响应已过期");
  return data;
}
export async function verifyQuoteReadIdentity(
  identity: QuoteReadIdentity,
  credential: QuoteReadCredential,
  signal?: AbortSignal,
): Promise<boolean> {
  return matchesQuoteReadIdentity(
    await quoteReadGet<unknown>("/v1/auth/me", credential, undefined, signal),
    identity,
  );
}
export { CanceledError };
